import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'urql'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import { formatEventDate } from 'app/lib/workflows/format'
import { TRIGGER_SAMPLE_ACTIVITIES_QUERY } from 'app/lib/workflows/graphql'
import { triggerObject } from 'app/lib/workflows/triggerCatalog'
import type { TriggerNodeData } from 'app/types/workflows'

/** A real recent record offered as a fill-in sample for testing a trigger. `fields` maps the trigger's
 *  field keys (binder + detail, incl. `answers.<name>`) to the record's real values. */
export type TriggerSample = {
  id: string
  label: string
  contactId?: string
  fields: Record<string, string>
}

type ActivityHit = {
  id: string
  typeId?: string | null
  type?: string | null
  subType?: string | null
  activityDate?: string | null
  contactName?: string | null
  contactPhone?: string | null
  source?: string | null
  vendor?: string | null
  channel?: string | null
  trackingNumberName?: string | null
  tags?: string[] | null
  contacts?: { id: string; name?: string | null }[] | null
}

type FormJsonRecord = {
  title?: string
  patientName?: string
  phone?: string
  treatment?: string
  requestedAppointmentDate?: string
  answers?: Record<string, unknown>
}

const MAX_SAMPLES = 8

const str = (value: unknown): string | undefined => {
  if (value == null || value === ``) return undefined
  if (Array.isArray(value)) return value.length ? value.join(`, `) : undefined
  return String(value)
}

/** The activity `type` to sample for this trigger, or undefined if it isn't activity-sourced. */
function sampleType(data: TriggerNodeData): { activitySourced: boolean; typeFilter?: string } {
  const object = triggerObject(data.kind)
  if (object === `call`) return { activitySourced: true, typeFilter: `Call` }
  // Form triggers ride the Form Event source, but recent Form ACTIVITIES are still the best
  // human-recognizable sample records (same underlying submissions).
  if (object === `form`) return { activitySourced: true, typeFilter: `Form` }
  if (object === `activity`) return { activitySourced: true, typeFilter: data.activityType }
  return { activitySourced: false }
}

/** Recent real records of the trigger's object, as "use a recent record" samples. Currently for
 *  activity-sourced triggers (Form/Call/Activity); a Form's detail (title/name/phone/answers) is
 *  resolved from its FormJson. Read-only — pulling a sample creates nothing. */
export function useTriggerSamples(data: TriggerNodeData): { samples: TriggerSample[]; loading: boolean } {
  const { activitySourced, typeFilter } = sampleType(data)
  const [result] = useQuery({
    query: TRIGGER_SAMPLE_ACTIVITIES_QUERY,
    variables: {
      search: typeFilter ? `type:${typeFilter}` : ``,
      pagination: { limit: 20 },
      sort: { field: `activityDate`, direction: `DESC` },
    },
    pause: !activitySourced,
  })
  const [samples, setSamples] = useState<TriggerSample[]>([])
  const [resolving, setResolving] = useState(false)

  // Base samples from the activity binder fields (no REST needed).
  const base = useMemo(() => {
    const hits = (result.data?.activities?.hits ?? []) as ActivityHit[]
    return hits.slice(0, MAX_SAMPLES).map((hit) => {
      const fields: Record<string, string> = {}
      const put = (key: string, value: unknown) => {
        const s = str(value)
        if (s) fields[key] = s
      }
      put(`type`, hit.type)
      put(`subType`, hit.subType)
      put(`activityDate`, hit.activityDate)
      put(`contactName`, hit.contactName)
      put(`contactPhone`, hit.contactPhone)
      put(`source`, hit.source)
      put(`vendor`, hit.vendor)
      put(`channel`, hit.channel)
      put(`trackingNumberName`, hit.trackingNumberName)
      put(`tags`, hit.tags)
      const who = hit.contactName || hit.contacts?.[0]?.name || hit.type || `Activity`
      // Call activities carry location wall-clock dates, not UTC (TRK-5396 by-design) — type-aware format.
      const when = hit.activityDate ? ` · ${formatEventDate(hit.activityDate, hit.type ?? undefined)}` : ``
      return {
        id: hit.id,
        typeId: hit.typeId ?? undefined,
        contactId: hit.contacts?.[0]?.id,
        label: `${who}${when}`,
        fields,
      }
    })
  }, [result.data])

  // Forms need their FormJson for the real detail (title, name, phone, answers).
  useEffect(() => {
    if (typeFilter !== `Form`) {
      setSamples(base.map(({ id, contactId, label, fields }) => ({ id, contactId, label, fields })))
      return
    }
    if (!base.length) {
      setSamples([])
      return
    }
    let alive = true
    setResolving(true)
    bg(async () => {
      const resolved = await Promise.all(
        base.map(async (item): Promise<TriggerSample> => {
          const fields = { ...item.fields }
          if (item.typeId) {
            try {
              const res = await window.fetch(`/api/activities/${item.id}/Form/${item.typeId}`)
              const json = (await res.json()) as { record?: FormJsonRecord } | FormJsonRecord
              const rec = (json as { record?: FormJsonRecord }).record ?? (json as FormJsonRecord)
              const put = (key: string, value: unknown) => {
                const s = str(value)
                if (s) fields[key] = s
              }
              put(`title`, rec.title)
              put(`patientName`, rec.patientName)
              put(`phone`, rec.phone)
              put(`treatment`, rec.treatment)
              put(`requestedAppointmentDate`, rec.requestedAppointmentDate)
              for (const [name, value] of Object.entries(rec.answers ?? {})) put(`answers.${name}`, value)
            } catch (err) {
              logError(`Form sample resolve failed`, err)
            }
          }
          const label =
            fields.patientName || fields.title
              ? [fields.patientName, fields.title].filter(Boolean).join(` · `)
              : item.label
          return { id: item.id, contactId: item.contactId, label, fields }
        }),
      )
      if (!alive) return
      setSamples(resolved)
      setResolving(false)
    })
    return () => {
      alive = false
    }
  }, [base, typeFilter])

  return { samples, loading: result.fetching || resolving }
}
